import { Book } from "./models/Book";
import { Subscription } from "./models/Subscription";

// PAGINATION =================================================================
export const PER_PAGE_OPTIONS = [15, 25, 50, 100] as const;

export type Pagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

export type PageParams = { page?: number; per_page?: number };

// SUBSCRIPTIONS ==============================================================
export type AdminSubscription = Subscription & {
  shopify_order_name: string | null;
  shopify_admin_url: string | null;
  is_gift?: boolean;
  offered_by_email?: string | null;
  offered_by_full_name?: string | null;
  gift_message?: string | null;
  created_at: string;
  user_email?: string | null;
  user_full_name?: string | null;
  user_subscriptions_count?: number;
};

export type SubscriptionAlerts = {
  several_per_user: number;
  several_books: number;
};

export type AdminSubscriptionsFilters = PageParams & {
  alert?: "several_per_user" | "several_books";
  status?: string;
  product?: string;
  search?: string;
};

export type AdminSubscriptionsResponse = {
  subscriptions: AdminSubscription[];
  pagination: Pagination;
  alerts: SubscriptionAlerts;
};

export type SubscriptionTargetParams = {
  email: string;
  first_name?: string;
  last_name?: string;
  send_invitation?: boolean;
  force?: boolean;
};

export type AdminUserListItem = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_admin: boolean;
  is_confirmed: boolean;
  is_locked: boolean;
  sign_in_count: number;
  last_sign_in_at: string | null;
  created_at: string;
  language: string | null;
  subscription: AdminSubscription | null;
  subscriptions_count: number;
  book_status: string | null;
  print_orders_count: number;
};

export type AdminUserStats = {
  answers_completed: number;
  questions_total: number;
  generated_chapters: number;
  anecdotes: number;
};

export type AdminPrintOrder = {
  id: number;
  user_id: number;
  book_id: number;
  first_name: string;
  last_name: string;
  address: string;
  address_line_2: string | null;
  city: string;
  zip_code: string;
  country: string;
  phone: string;
  quantity: number | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  is_confirmed: boolean;
  delivery_address: string;
  user_email: string | null;
  user_full_name: string | null;
  book_title: string | null;
  book_status: string | null;
  book_pdf_url: string | null;
  cover_pdf_url: string | null;
  printer_reference: string;
  printer_order_id: string | null;
  sent_to_printer_at: string | null;
  printer_error: string | null;
  printer_status: string | null;
  printer_carrier: string | null;
  printer_tracking_url: string | null;
  printer_status_updated_at: string | null;
};

export type AdminPrinterApiStatus = {
  configured: boolean;
  partner?: { clientId: string; name: string; slug: string };
  error?: string;
};

export type AdminAuditLog = {
  id: number;
  admin: string | null;
  action: string;
  target_type?: string | null;
  target_id?: number | null;
  details: Record<string, unknown>;
  created_at: string;
};

export type AdminUserDetail = {
  user: AdminUserListItem;
  subscription: AdminSubscription | null;
  subscriptions: AdminSubscription[];
  book: Book | null;
  print_orders: AdminPrintOrder[];
  stats: AdminUserStats;
  invitation: {
    scheduled_at: string | null;
    sent_at: string | null;
    accepted_at: string | null;
  };
  sign_in: {
    current_at: string | null;
    current_ip: string | null;
    last_ip: string | null;
    failed_attempts: number;
  };
  audit_logs: AdminAuditLog[];
};

export type AdminUserCreateParams = {
  user: {
    email: string;
    first_name?: string;
    last_name?: string;
    language_id?: number;
  };
  product: "pdf" | "print";
  with_audiobook: boolean;
  quantity: number;
  send_invitation: boolean;
  shopify_order?: string;
  shopify_order_name?: string;
};

export type AdminUsersResponse = {
  users: AdminUserListItem[];
  pagination: Pagination;
};

export type AdminPrintOrdersResponse = {
  print_orders: AdminPrintOrder[];
  pagination: Pagination;
};

export type AdminDashboardStats = {
  users: {
    total: number;
    admins: number;
    confirmed: number;
    last_30_days: number;
  };
  subscriptions: {
    active: number;
    pending: number;
    cancelled: number;
    print: number;
    pdf: number;
    with_audiobook: number;
  };
  books: Record<string, number>;
  print_orders: {
    total: number;
    pending: number;
    confirmed: number;
    books_to_print: number;
  };
  recent_users: AdminUserListItem[];
  stuck_books: AdminStuckBook[];
  subscription_alerts: SubscriptionAlerts;
};

export type AdminStuckBook = {
  id: number;
  title: string | null;
  status: string;
  updated_at: string;
  user_id: number;
  user_email: string | null;
  /** crashed: the generation failed; overdue: still running after the delay */
  reason: "crashed" | "overdue";
  stuck_after_minutes: number | null;
};

export type AdminUsersFilters = PageParams & {
  search?: string;
  multiple_subscriptions?: boolean;
  subscription_status?: string;
  subscription_product?: string;
  /** A book status, or "none" for users without a book */
  book_status?: string;
  admins_only?: boolean;
};

export type AdminUserUpdateParams = {
  first_name?: string;
  last_name?: string;
  email?: string;
  is_admin?: boolean;
  language_id?: number;
};

export type AdminSubscriptionUpdateParams = {
  status?: string;
  product?: string;
  with_audiobook?: boolean;
  books_purchased?: number;
  books_remaining?: number;
  books_printed?: number;
  shopify_order_id?: string;
  shopify_order_name?: string;
};

export type AdminPrintOrderUpdateParams = {
  first_name?: string;
  last_name?: string;
  address?: string;
  address_line_2?: string;
  city?: string;
  zip_code?: string;
  country?: string;
  phone?: string;
  quantity?: number;
  confirmed_at?: string | null;
};

// BOOKS ======================================================================
export type AdminAttachment = {
  url: string;
  filename: string;
  byte_size: number;
  created_at: string;
};

export type AdminAttachmentName =
  "cover_image" | "content_pdf" | "cover_pdf" | "numeric_pdf" | "zip_file";

export type AdminBook = {
  id: number;
  user_id: number;
  title: string | null;
  status: string;
  author: string | null;
  author_birthdate: string | null;
  publication_date: string | null;
  summary: string | null;
  total_page_count: number | null;
  cover_type: string | null;
  cover_text_color: string | null;
  cover_bg_color: string | null;
  language_id: number | null;
  narration_style_id: number | null;
  language: string | null;
  narration_style: string | null;
  audio_book_config: Record<string, unknown>;
  audio_book_token: string | null;
  batch_id: string | null;
  msg_batch_ids: unknown;
  ai_model: string | null;
  created_at: string;
  updated_at: string;
  attachments: Record<AdminAttachmentName, AdminAttachment | null>;
  user_voice_samples: { id: number; filename: string; url: string }[];
};

export type AdminMedium = {
  id: number;
  title: string | null;
  description: string | null;
  file_type: string;
  url: string | null;
  filename: string | null;
  byte_size: number | null;
  created_at: string;
};

export type AdminGeneratedChapter = {
  id: number;
  book_id: number;
  chapter_id: number;
  chapter_number: number | null;
  title: string | null;
  content: string | null;
  completed: boolean;
  word_count: number | null;
  is_audio_generated: boolean;
  audio_url: string | null;
  media: AdminMedium[];
  updated_at: string;
};

export type AdminAnswer = {
  id: number;
  question_id: number;
  question_number: number | null;
  chapter_number: number | null;
  question: string | null;
  content: string | null;
  completed: boolean | null;
  updated_at: string;
};

export type AdminAnecdote = {
  id: number;
  user_id: number;
  book_id: number;
  generated_chapter_id: number;
  chapter_number: number;
  friend_email: string;
  friend_first_name: string;
  friend_last_name: string;
  custom_message: string | null;
  status: string;
  content: string | null;
  marketing_accepted: boolean;
  token: string;
  submitted_at: string | null;
  expires_at: string;
  last_reminder_sent_at: string | null;
  created_at: string;
  author_name: string;
  photo_url: string | null;
  expired: boolean;
};

export type AdminBookDetail = {
  book: AdminBook;
  /** Crash of the current PDF generation (book still in pdf_generating) */
  last_pdf_failure: { error: string; at: string } | null;
  /** Failure of the current AI generation (job crash or failed batch requests) */
  last_ai_failure: {
    error: string;
    at: string;
    batch_id: string | null;
  } | null;
  /** Live state of the Claude batch, when the book has one */
  ai_batch: AdminAiBatch | null;
  user: AdminUserListItem;
  statuses: string[];
  pdf_sizes: string[];
  pdf_default_size: string;
  claude: {
    models: Record<string, ClaudeModel>;
    model: string;
    effort: string;
  };
  generated_chapters: AdminGeneratedChapter[];
  answers: AdminAnswer[];
  anecdotes: AdminAnecdote[];
  print_orders: AdminPrintOrder[];
};

export type AdminBookUpdateParams = Partial<
  Pick<
    AdminBook,
    | "title"
    | "author"
    | "author_birthdate"
    | "publication_date"
    | "summary"
    | "status"
    | "total_page_count"
    | "cover_type"
    | "cover_text_color"
    | "cover_bg_color"
    | "language_id"
    | "narration_style_id"
  >
>;

// INFRA ======================================================================
export type HerokuFormation = {
  type: string;
  size: string;
  quantity: number;
  command: string;
  updated_at: string;
};

export type HerokuDyno = {
  name: string;
  type: string;
  size: string;
  state: string;
  command: string;
  created_at: string;
  updated_at: string;
};

export type AdminInfra =
  | { enabled: false }
  | {
      enabled: true;
      app_name: string;
      sizes: string[];
      pdf_size: string;
      formation: HerokuFormation[];
      dynos: HerokuDyno[];
    };

// JOBS =======================================================================
export type AdminJobSet = "retry" | "dead" | "scheduled";

export type AdminJob = {
  jid: string;
  klass: string;
  args: unknown[];
  queue: string;
  at: string | null;
  enqueued_at: string | null;
  error_class: string | null;
  error_message: string | null;
  retry_count: number | null;
};

export type AdminJobs = {
  stats: {
    processed: number;
    failed: number;
    enqueued: number;
    retries: number;
    dead: number;
    scheduled: number;
  };
  queues: { name: string; size: number; latency: number }[];
  processes: {
    hostname: string;
    queues: string[];
    concurrency: number;
    busy: number;
    started_at: number;
  }[];
  busy: { klass: string; args: unknown[]; queue: string; run_at: number }[];
  set: AdminJobSet;
  set_sizes: Record<AdminJobSet, number>;
  jobs: AdminJob[];
  pagination: Pagination;
};

// CONTENT ====================================================================
export type AdminContentResource =
  "chapters" | "questions" | "languages" | "narration_styles";

export type AdminContentRecord = { id: number } & Record<string, unknown>;

// SEARCH / AUDIT =============================================================
export type AdminSearchResult = {
  type: "user" | "book" | "subscription" | "anecdote";
  id: number;
  label: string;
  hint: string | null;
  user_id: number | null;
};

export type AdminAuditLogsResponse = {
  logs: AdminAuditLog[];
  pagination: Pagination;
};

// SETTINGS ===================================================================
export type ClaudeModel = {
  label: string;
  input_price: number;
  output_price: number;
  efforts: string[];
  default_effort: string;
  note: string;
};

export type AdminAiBatch = {
  id: string;
  status?: "in_progress" | "canceling" | "ended";
  request_counts?: {
    processing: number;
    succeeded: number;
    errored: number;
    canceled: number;
    expired: number;
  };
  created_at?: string;
  ended_at?: string | null;
  /** Deadline to process the requests: the unprocessed ones expire then */
  expires_at?: string;
  /** Results of an ended batch can be fetched until then */
  results_until?: string;
  /** The batch could not be read from the Claude API */
  error?: string;
};

export type AdminSettings = {
  claude: {
    models: Record<string, ClaudeModel>;
    default_model: string;
    model: string;
    effort: string;
  };
  pdf: {
    sizes: string[];
    default_size: string;
    size: string;
  };
};
